import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled from 'styled-components';
import {
  brandColours,
  fontSize,
  maxBreakpointQuery,
  minBreakpointQuery,
  visuallyHidden,
  standardTransition,
  sectionPaddings,
  breakpointSizes,
  fluidFontSize,
} from '../styles';
import { Container, RichText, Link, Svg } from './ui';
import BeeBadge from './BeeBadge';
import Logo from '../images/logo.svg';
import facebookIcon from '../images/facebook-icon.inline.svg';
import instagramIcon from '../images/instagram-icon.inline.svg';
import twitterXIcon from '../images/twitterx-icon.inline.svg';
import tikTokIcon from '../images/tiktok-icon.inline.svg';
import linkedInIcon from '../images/linkedin-icon.inline.svg';

const StyledFooter = styled.footer`
  ${sectionPaddings('40px', '75px')};
`;

const StyledContainer = styled(Container)`
  max-width: 1000px;
`;

const StyledInner = styled.div`
  position: relative;

  ${minBreakpointQuery.medium`
    padding-top: 130px;
  `}

  ${minBreakpointQuery.large`
    padding-top: 160px;
  `}
`;

const StyledBadge = styled(BeeBadge)`
  ${maxBreakpointQuery.medium`
    ${visuallyHidden()}
  `}

  ${minBreakpointQuery.medium`
    position: absolute;
    top: 0;
    right: 0;
    max-width: 200px;
  `}

  ${minBreakpointQuery.large`
    max-width: 220px;
  `}

  ${minBreakpointQuery.xxlarge`
    max-width: 240px;
  `}
`;

const StyledLogo = styled.img`
  display: block;
  margin: 0 auto;
  width: 205px;

  ${minBreakpointQuery.small`
    width: 260px;
  `}

  ${minBreakpointQuery.medium`
    width: 315px;
  `}
`;

const StyledText = styled.div`
  margin-top: 25px;
  text-align: center;

  ${minBreakpointQuery.small`
    margin-top: 35px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 45px;
  `}

  p {
    ${fluidFontSize(
      '26px',
      '46px',
      breakpointSizes.tiny,
      breakpointSizes.xxlarge,
    )};
  }
`;

const StyledLinksBlocks = styled.nav`
  display: grid;
  gap: 30px;
  grid-template-columns: repeat(2, 1fr);
  margin-top: 40px;

  ${minBreakpointQuery.small`
    gap: 40px;
    margin-top: 50px;
  `}

  ${minBreakpointQuery.medium`
    grid-template-columns: repeat(4, 1fr);
  `}

  ${minBreakpointQuery.large`
    gap: 50px;
    margin-top: 60px;
    padding-right: 20px;
    padding-left: 20px;
  `}

  ${minBreakpointQuery.xxlarge`
    gap: 60px;
    margin-top: 70px;
    padding-right: 40px;
    padding-left: 40px;
  `}

  ${minBreakpointQuery.xxxxlarge`
    gap: 70px;
    margin-top: 80px;
    padding-right: 60px;
    padding-left: 60px;
  `}
`;

const StyledLinksBlock = styled.div``;

const StyledHeading = styled.h3`
  margin-bottom: 18px;
  ${fontSize(15)};

  ${minBreakpointQuery.small`
    ${fontSize(16)};
  `}

  ${minBreakpointQuery.large`
    ${fontSize(17)};
  `}
`;

const StyledLinks = styled.ul`
  display: grid;
  gap: 6px;
`;

const StyledLinkItem = styled.li``;

const StyledLink = styled(Link)`
  ${fontSize(15)};
  line-height: 1.2;

  ${minBreakpointQuery.small`
    ${fontSize(16)};
  `}

  ${minBreakpointQuery.large`
    ${fontSize(17)};
  `}
`;

const StyledSocialLinks = styled.ul`
  justify-content: center;
  display: flex;
  gap: 20px;
  margin-top: 40px;

  ${minBreakpointQuery.small`
    margin-top: 50px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 60px;
  `}
`;

const StyledSocialLink = styled(Link)`
  display: block;
`;

const StyledSocialIcon = styled(Svg)`
  display: block;
  height: 43px;
  width: 43px;
  fill: ${brandColours.quaternary};
  transition: ${standardTransition('fill')};

  &:hover {
    fill: ${brandColours.secondary};
  }

  ${minBreakpointQuery.small`
    height: 49px;
    width: 49px;
  `}

  ${minBreakpointQuery.large`
    height: 55px;
    width: 55px;
  `}
`;

const StyledAddress = styled.address`
  text-align: center;
  font-style: normal;
  line-height: 1.5;
  ${fontSize(12)};
  margin-top: 40px;

  ${minBreakpointQuery.small`
    margin-top: 50px;
    ${fontSize(14)};
  `}

  ${minBreakpointQuery.large`
    margin-top: 60px;
  `}
`;

const Footer = () => {
  const {
    contentfulSitewide: {
      text,
      aboutLinks,
      legalLinks,
      helpLinks,
      accountLinks,
      facebookLink,
      instagramLink,
      twitterXLink,
      tikTokLink,
      linkedInLink,
    },
  } = useStaticQuery(graphql`
    query FooterQuery {
      contentfulSitewide {
        text: footerText {
          raw
        }
        aboutLinks: footerAboutLinks {
          text
          pageUrl {
            ...LinkFragment
          }
        }
        legalLinks: footerLegalLinks {
          text
          pageUrl {
            ...LinkFragment
          }
        }
        helpLinks: footerHelpLinks {
          text
          pageUrl {
            ...LinkFragment
          }
        }
        accountLinks: footerAccountLinks {
          text
          pageUrl {
            ...LinkFragment
          }
        }
        facebookLink {
          ...LinkFragment
        }
        instagramLink {
          ...LinkFragment
        }
        twitterXLink {
          ...LinkFragment
        }
        tikTokLink {
          ...LinkFragment
        }
        linkedInLink {
          ...LinkFragment
        }
      }
    }
  `);

  const linksBlocks = [
    {
      heading: 'About Ecotalk',
      links: aboutLinks,
    },
    {
      heading: 'The Legals',
      links: legalLinks,
    },
    {
      heading: 'Help',
      links: helpLinks,
    },
    {
      heading: 'Account',
      links: accountLinks,
    },
  ];

  const socialLinks = [
    {
      name: 'Facebook',
      icon: facebookIcon,
      link: facebookLink,
    },
    {
      name: 'Instagram',
      icon: instagramIcon,
      link: instagramLink,
    },
    {
      name: 'Twitter / X',
      icon: twitterXIcon,
      link: twitterXLink,
    },
    {
      name: 'TikTok',
      icon: tikTokIcon,
      link: tikTokLink,
    },
    {
      name: 'LinkedIn',
      icon: linkedInIcon,
      link: linkedInLink,
    },
  ];

  return (
    <StyledFooter>
      <StyledContainer>
        <StyledInner>
          <StyledBadge isFooter={true} />
          <StyledLogo src={Logo} alt="ecotalk Logo" loading="lazy" />
          <StyledText>
            <RichText content={text} />
          </StyledText>
          <StyledLinksBlocks>
            {linksBlocks.map(({ heading, links }, id) => (
              <StyledLinksBlock key={id}>
                <StyledHeading>{heading}</StyledHeading>
                <StyledLinks>
                  {links.map(({ pageUrl, text }, id) => (
                    <StyledLinkItem key={id}>
                      <StyledLink to={pageUrl}>{text}</StyledLink>
                    </StyledLinkItem>
                  ))}
                </StyledLinks>
              </StyledLinksBlock>
            ))}
          </StyledLinksBlocks>
          <StyledSocialLinks>
            {socialLinks.map(({ name, icon, link }, id) => (
              <StyledLinkItem key={id}>
                <StyledSocialLink
                  to={link}
                  title={`View ${name} Profile`}
                  aria-label={`View ${name} Profile`}
                >
                  <StyledSocialIcon image={icon} />
                </StyledSocialLink>
              </StyledLinkItem>
            ))}
          </StyledSocialLinks>
          <StyledAddress>
            Ecotalk Limited | Registered office: Lion House, Rowcroft, Stroud,
            Gloucestershire, GL5 3BY | Registered in England & Wales |
            Registered company number: 007442414 | VAT Registered: 465434578
          </StyledAddress>
        </StyledInner>
      </StyledContainer>
    </StyledFooter>
  );
};

export default Footer;
